import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ListViewHOC from 'components/ListView';
import DefaultListView from 'components/ListView/DefaultListView';
import LoadingWrapper from 'components/LoadingWrapper';
import { useCustomerNavigate } from 'hooks/useCustomerNavigate';
import { useNumericIdRouteParam } from 'hooks/useNumericIdRouteParam';
import { fetchRolePermissions } from 'slices/rolePermission.slice';
import {
  copyRole,
  createRole,
  deleteRole,
  fetchRoles,
  roleSelector,
  rolesLoading,
  rolesSelector,
  saveRole,
} from 'slices/roles.slice';
import { useAppDispatch } from 'slices/store';
import { errorNotification } from 'utils/notifications';
import type { TRole } from '@timeedit/types';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { theme } from '@timeedit/ui-components/antd';
import RoleDetailPage from './detail';

const RolePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useCustomerNavigate();
  const resourceId = useNumericIdRouteParam();
  const { token } = theme.useToken();

  /**
   * SELECTORS
   */
  const roles = useSelector(rolesSelector);
  const originalRoleEntity = useSelector(roleSelector(resourceId));
  const isLoading = useSelector(rolesLoading);

  /**
   * STATE
   */
  const [roleEntity, setRoleEntity] = useState<TRole>(originalRoleEntity);

  /**
   * EFFECTS
   */
  useEffect(() => {
    // Jumping to newly saved new config
    if (resourceId === 0 && roles.findIndex((el: TRole) => el.id === 0) === -1)
      navigate(`/user-management/roles/${roles[roles.length - 1].id}`, {
        replace: true,
      });
    // Default case
    if (resourceId == null && roles && roles[0] && roles[0].id)
      navigate(`/user-management/roles/${roles[0].id}`, { replace: true });
  }, [resourceId, roles, navigate]);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchRolePermissions());
  }, []);

  useEffect(() => {
    if (resourceId !== roleEntity?.id && originalRoleEntity) {
      setRoleEntity(originalRoleEntity);
    }
  }, [resourceId, roles]);

  /**
   * EVENT HANDLERS
   */
  const onSelectRole = (roleId: string | number) => {
    navigate(`/user-management/roles/${roleId}`);
  };

  // Adds new authentication config
  const onAddRole = () => dispatch(createRole());

  // Deletes chosen authentication config
  const onDeleteRole = (roleIdToDelete: string | number) => {
    dispatch(deleteRole(roleIdToDelete as number));
    if (resourceId === roleIdToDelete) navigate('/user-management/roles');
  };

  // Handle changes
  const onChange = (prop: string, value: any) => {
    setRoleEntity({ ...roleEntity, [prop]: value });
  };

  // Copy
  const onCopy = (resourceId: number) => dispatch(copyRole(resourceId));

  // Add role permission
  const onAddRolePermission = (rolePermissionId: number) => {
    setRoleEntity({
      ...roleEntity,
      rolePermissions: [...roleEntity.rolePermissions, rolePermissionId],
    });
  };

  // Delete role permission
  const onDeleteRolePermission = (rolePermissionId: number) => {
    const idx = roleEntity.rolePermissions.indexOf(rolePermissionId);
    if (idx > -1) {
      const updRolePermissionIds = [
        ...roleEntity.rolePermissions.slice(0, idx),
        ...roleEntity.rolePermissions.slice(idx + 1),
      ];
      setRoleEntity({ ...roleEntity, rolePermissions: updRolePermissionIds });
    }
  };
  const handleSaveRole = () => {
    if (roleEntity.name.includes('|')) {
      errorNotification('Role name cannot contain "|"');
      return;
    }
    dispatch(saveRole(roleEntity));
  };

  return (
    <DefaultContentLayout
      title="Roles"
      subtitle="Manage roles for the organization"
      actions={
        <>
          <Button
            onClick={() =>
              originalRoleEntity && setRoleEntity(originalRoleEntity)
            }
          >
            {'Discard'}
          </Button>
          <Button type="primary" onClick={handleSaveRole}>
            {'Save'}
          </Button>
        </>
      }
    >
      <div
        style={{ display: 'flex', height: '100%', marginTop: token.marginXS }}
      >
        <ListViewHOC
          sectionHeading="Roles"
          number={(roles || []).length}
          onSelect={onAddRole}
        >
          <DefaultListView
            data={(roles || []).map((role) => ({
              listLabel: role.name || '',
              value: role.id,
            }))}
            onSelect={onSelectRole}
            onDelete={onDeleteRole}
            onCopy={onCopy}
            selectedItemId={resourceId}
          />
        </ListViewHOC>
        <div className="inner-content--wrapper allow-scroll">
          <LoadingWrapper isLoading={isLoading}>
            {resourceId !== undefined && roleEntity && (
              <RoleDetailPage
                onChange={onChange}
                role={roleEntity}
                onAddRolePermission={onAddRolePermission}
                onDeleteRolePermission={onDeleteRolePermission}
              />
            )}
          </LoadingWrapper>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default RolePage;
